import styled from "@emotion/styled/macro";
import {
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

const VIEWPORT_MARGIN_PX = 16;
const TRIGGER_GAP_PX = 10;
const ARROW_SIZE_PX = 8;
const ARROW_EDGE_INSET_PX = 20;

type TooltipPlacement = "top" | "bottom";

type TooltipPosition = {
  top: number;
  left: number;
  width: number;
  arrowLeft: number;
  placement: TooltipPlacement;
};

type OfferReviewInfoTooltipProps = {
  content: ReactNode;
  ariaLabel: string;
  maxWidth?: number;
  iconSize?: number;
  iconColor?: string;
  testId?: string;
  className?: string;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const OfferReviewInfoTooltip = ({
  content,
  ariaLabel,
  maxWidth = 320,
  iconSize = 16,
  iconColor = tokens.BRAND_HERO_BLUE,
  testId,
  className,
}: OfferReviewInfoTooltipProps) => {
  const tooltipId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>();
  const isOpen = isHovered || isFocused || isPinned;

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) {
      return;
    }

    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(maxWidth, viewportWidth - VIEWPORT_MARGIN_PX * 2);
    bubble.style.width = `${width}px`;
    const height = bubble.offsetHeight;

    const triggerRect = trigger.getBoundingClientRect();
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const left = clamp(
      triggerCenterX - width / 2,
      VIEWPORT_MARGIN_PX,
      viewportWidth - VIEWPORT_MARGIN_PX - width
    );

    const spaceAbove = triggerRect.top - TRIGGER_GAP_PX - VIEWPORT_MARGIN_PX;
    const spaceBelow =
      viewportHeight - triggerRect.bottom - TRIGGER_GAP_PX - VIEWPORT_MARGIN_PX;
    const placement: TooltipPlacement =
      height <= spaceAbove || spaceAbove >= spaceBelow ? "top" : "bottom";
    const top = clamp(
      placement === "top"
        ? triggerRect.top - TRIGGER_GAP_PX - height
        : triggerRect.bottom + TRIGGER_GAP_PX,
      VIEWPORT_MARGIN_PX,
      Math.max(VIEWPORT_MARGIN_PX, viewportHeight - VIEWPORT_MARGIN_PX - height)
    );

    setPosition({
      top,
      left,
      width,
      arrowLeft: clamp(
        triggerCenterX - left,
        ARROW_EDGE_INSET_PX,
        width - ARROW_EDGE_INSET_PX
      ),
      placement,
    });
  }, [maxWidth]);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsHovered(false);
        setIsPinned(false);
        triggerRef.current?.blur();
      }
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (!triggerRef.current?.contains(event.target as Node)) {
        setIsPinned(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isOpen]);

  return (
    <Wrapper className={className}>
      <Trigger
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-describedby={tooltipId}
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") {
            setIsHovered(true);
          }
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === "mouse") {
            setIsHovered(false);
          }
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onClick={() => setIsPinned((pinned) => !pinned)}
      >
        <SvgIcon
          name={SvgNames.InfoOutline}
          width={iconSize}
          height={iconSize}
          color={iconColor}
        />
      </Trigger>
      {createPortal(
        <Bubble
          ref={bubbleRef}
          id={tooltipId}
          role="tooltip"
          data-testid={testId ? `${testId}-content` : undefined}
          $isVisible={isOpen && position !== undefined}
          style={
            position
              ? {
                  top: position.top,
                  left: position.left,
                  width: position.width,
                }
              : undefined
          }
        >
          <Typography color={tokens.INFO_30}>{content}</Typography>
          <Arrow
            data-testid={testId ? `${testId}-arrow` : undefined}
            $placement={position?.placement ?? "top"}
            style={{ left: position?.arrowLeft }}
          />
        </Bubble>,
        document.body
      )}
    </Wrapper>
  );
};

const Wrapper = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  pointer-events: auto;
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  color: inherit;

  &:focus-visible {
    outline: 2px solid ${tokens.BRAND_HERO_BLUE};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const Bubble = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  box-sizing: border-box;
  padding: 24px;
  border-radius: 14px;
  background: ${tokens.INFO_80};
  box-shadow: 0 4px 16px
    color-mix(in srgb, ${tokens.TINTS_HERO_BLUE_20} 16%, transparent);
  pointer-events: none;
  visibility: ${({ $isVisible }) => ($isVisible ? "visible" : "hidden")};
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 100ms ease-in-out;
`;

const Arrow = styled.span<{ $placement: TooltipPlacement }>`
  position: absolute;
  width: 0;
  height: 0;
  transform: translateX(-50%);
  border: ${ARROW_SIZE_PX}px solid transparent;

  ${({ $placement }) =>
    $placement === "top"
      ? `
    top: 100%;
    border-top-color: ${tokens.INFO_80};
  `
      : `
    bottom: 100%;
    border-bottom-color: ${tokens.INFO_80};
  `}
`;

export { OfferReviewInfoTooltip };
